import { useState, useCallback } from 'react'
import { Plus, Lock } from 'lucide-react'
import { router } from '@inertiajs/react'
import { FormSection } from '@/components/backoffice/FormSection'
import { FieldFormSheet } from '@/components/backoffice/FieldFormSheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'multi_select' | 'currency' | 'attachment'

type Field = {
  id: string
  field_type: FieldType
  label: string
  identifier: string
  required: boolean
  encrypted: boolean
  description?: string | null
  position: number
  options: { id: string; label: string; value: string; position: number }[]
  config: Record<string, unknown>
}

interface FieldEditorProps {
  fieldsApiPath: string
  orderingApiPath: string
  fields: Field[]
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}

async function apiRequest(url: string, method: string, body?: Record<string, unknown>): Promise<{ ok: boolean; data?: any; errors?: string[] }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  })

  if (res.status === 204) return { ok: true }

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    return { ok: false, errors: data?.errors || [res.statusText] }
  }

  return { ok: true, data }
}

export function FieldEditor({ fieldsApiPath, orderingApiPath, fields }: FieldEditorProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingField, setEditingField] = useState<Field | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const reloadPage = useCallback(() => {
    router.reload({ only: ['fields'] })
  }, [])

  function openAddSheet() {
    setEditingField(null)
    setSheetOpen(true)
  }

  function openEditSheet(field: Field) {
    setEditingField(field)
    setSheetOpen(true)
  }

  async function handleSave(data: {
    field_type: string
    label: string
    required: boolean
    encrypted: boolean
    description: string | null
    placeholder: string | null
  }) {
    if (editingField) {
      const result = await apiRequest(`${fieldsApiPath}/${editingField.id}`, 'PATCH', {
        label: data.label,
        required: data.required,
        encrypted: data.encrypted,
        description: data.description,
        placeholder: data.placeholder,
      })
      if (result.ok) reloadPage()
      return result
    } else {
      const result = await apiRequest(fieldsApiPath, 'POST', {
        field_type: data.field_type,
        label: data.label,
        required: data.required,
        encrypted: data.encrypted,
        description: data.description,
        placeholder: data.placeholder,
      })
      if (result.ok) reloadPage()
      return result
    }
  }

  const handleRemoveField = async (fieldId: string) => {
    if (!confirm('Remove this field?')) return
    setApiError(null)

    const result = await apiRequest(`${fieldsApiPath}/${fieldId}`, 'DELETE')

    if (result.ok) {
      reloadPage()
    } else {
      setApiError(result.errors?.join(', ') || 'Failed to remove field')
    }
  }

  const handleMoveUp = async (fieldId: string) => {
    const index = fields.findIndex(f => f.id === fieldId)
    if (index > 0) {
      const newFields = [...fields]
      const temp = newFields[index]
      newFields[index] = newFields[index - 1]
      newFields[index - 1] = temp
      const positions: Record<string, number> = {}
      newFields.forEach((f, i) => { positions[f.id] = i })
      await apiRequest(orderingApiPath, 'PATCH', { positions })
      reloadPage()
    }
  }

  const handleMoveDown = async (fieldId: string) => {
    const index = fields.findIndex(f => f.id === fieldId)
    if (index < fields.length - 1) {
      const newFields = [...fields]
      const temp = newFields[index]
      newFields[index] = newFields[index + 1]
      newFields[index + 1] = temp
      const positions: Record<string, number> = {}
      newFields.forEach((f, i) => { positions[f.id] = i })
      await apiRequest(orderingApiPath, 'PATCH', { positions })
      reloadPage()
    }
  }

  return (
    <>
      <FormSection title="Application Fields" description="Define what information applicants must provide">
        <div className="space-y-3">
          {apiError && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20">
              {apiError}
            </div>
          )}

          {fields.length === 0 && (
            <div className="text-center py-6 text-muted-foreground border rounded-lg bg-muted/30">
              <p className="text-sm">No fields defined yet.</p>
            </div>
          )}

          <div className="space-y-2">
            {fields.map((field) => (
              <div key={field.id} className="group flex items-center gap-3 p-3 bg-card rounded-lg border">
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    onClick={() => handleMoveUp(field.id)}
                    disabled={fields.indexOf(field) === 0}
                  >
                    &#9650;
                  </button>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    onClick={() => handleMoveDown(field.id)}
                    disabled={fields.indexOf(field) === fields.length - 1}
                  >
                    &#9660;
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{field.label}</span>
                    <Badge variant="outline" className="text-[10px]">{field.field_type}</Badge>
                    {field.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
                    {field.encrypted && <Lock className="h-3 w-3 text-muted-foreground" />}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{field.identifier}</p>
                </div>
                <div className="flex gap-1 text-muted-foreground">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEditSheet(field)} type="button">
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => handleRemoveField(field.id)} type="button">
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={openAddSheet} type="button">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Field
          </Button>
        </div>
      </FormSection>

      <FieldFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        field={editingField}
        onSave={handleSave}
      />
    </>
  )
}
